// Vícejazyčnost zákaznické části (administrace zůstává česky) – stejný princip jako konfigurátor diářů.
// Jazyk se pozná z webu, ze kterého zákazník přišel (doména e-shopu), případně z parametru ?lang=.
// Texty v aplikaci jsou česky; tato vrstva je za běhu přeloží podle slovníku (klíč = český text).
import { D, P } from './i18nDict';

export const LANGS = ['cs', 'sk', 'pl', 'en', 'de', 'it', 'es', 'hu'] as const;
export type Lang = typeof LANGS[number];
export const LANG_LABELS: Record<Lang, string> = { cs: 'CZ', sk: 'SK', pl: 'PL', en: 'EN', de: 'DE', it: 'IT', es: 'ES', hu: 'HU' };
// pořadí překladů ve slovníku: [sk, pl, en, de, it, es, hu]
const IDX: Record<string, number> = { sk: 0, pl: 1, en: 2, de: 3, it: 4, es: 5, hu: 6 };

// doména e-shopu → jazyk
const HOSTS: [RegExp, Lang][] = [
  [/(^|\.)aaapapir\.cz$/i, 'cs'], [/(^|\.)aaapapir\.sk$/i, 'sk'],
  [/(^|\.)aaakalendarze\.pl$/i, 'pl'], [/(^|\.)aaakalender\.de$/i, 'de'],
  [/(^|\.)aaapapir\.co\.uk$/i, 'en'], [/(^|\.)aaapapir\.com$/i, 'en'],
  [/\.cz$/i, 'cs'], [/\.sk$/i, 'sk'], [/\.pl$/i, 'pl'], [/\.(de|at)$/i, 'de'], [/\.(co\.uk|uk|com)$/i, 'en'],
  [/\.it$/i, 'it'], [/\.es$/i, 'es'], [/\.hu$/i, 'hu'],
];
const isLang = (l: string): l is Lang => (LANGS as readonly string[]).includes(l);
const langOfHost = (h: string): Lang | null => { for (const [re, l] of HOSTS) if (re.test(h || '')) return l; return null; };
export const langOfUrl = (u: string): Lang | null => {
  try { const x = new URL(u); const q = (x.searchParams.get('lang') || '').toLowerCase(); if (isLang(q)) return q; return langOfHost(x.hostname); } catch { return null; }
};
function detect(): Lang {
  const sp = new URLSearchParams(location.search);
  const q = (sp.get('lang') || '').toLowerCase();
  if (isLang(q)) return q;
  const urls: string[] = [];
  const z = sp.get('zdroj'); if (z && /^https?:\/\//i.test(z)) urls.push(z); // testovací adresa e-shopu
  try { if (window.top !== window) urls.push(window.top!.location.href); } catch { /* cizí doména */ }
  try { const ao = (location as any).ancestorOrigins; if (ao) for (let i = ao.length - 1; i >= 0; i--) urls.push(ao[i]); } catch { /* */ }
  if (document.referrer) urls.push(document.referrer);
  for (const u of urls) { const l = langOfUrl(u); if (l && !/samolepky|run\.app/i.test(new URL(u).hostname)) return l; }
  return 'cs';
}

let LANG: Lang = detect();
let idx = IDX[LANG];
document.documentElement.lang = LANG;

const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
const look = (k: string): string | null => { const a = D[k]; return a ? (a[idx] ?? null) : null; };
const COLLECT = /[?&]i18n_collect=1/.test(location.search);
const missing = new Set<string>();
if (COLLECT) (window as any).__i18nMissing = missing;

export function t(s: string): string {
  if (LANG === 'cs' || s == null) return s;
  const str = String(s); const k = norm(str); if (!k) return str;
  let out = look(k);
  if (out == null) for (const [re, f] of P) { const m = k.match(re); if (m) { const r = f(m, idx, look, t); if (r != null) { out = r; break; } } }
  if (out == null) { if (COLLECT && /[A-Za-zÀ-ž]{2,}/.test(k)) missing.add(k); return str; }
  const lead = str.match(/^\s*/)![0], trail = str.match(/\s*$/)![0];
  return lead + out + trail;
}

// původní (český) text každého přeloženého uzlu → jazyk lze přepnout za chodu (náhled pro administrátora)
const ORIG = new WeakMap<Node, { cs: string; out: string }>();
const ORIGA = new WeakMap<Element, Record<string, { cs: string; out: string }>>();
const ATTRS = ['placeholder', 'title', 'aria-label', 'alt'];
const skip = (el: Element | null) => !el || !!el.closest('.rda, svg, script, style, [data-noi18n], [contenteditable]');
function trText(n: Text) {
  if (skip(n.parentElement)) return;
  const v = n.nodeValue || ''; let o = ORIG.get(n);
  if (!o || v !== o.out) { if (!v || !/[A-Za-zÀ-ž]/.test(v)) return; o = { cs: v, out: v }; ORIG.set(n, o); }
  const tt = t(o.cs); o.out = tt; if (tt !== v) n.nodeValue = tt;
}
function trAttrs(el: Element) {
  if (skip(el)) return;
  let m = ORIGA.get(el);
  for (const a of ATTRS) {
    const v = el.getAttribute(a); if (!v) continue;
    if (!m) { m = {}; ORIGA.set(el, m); }
    let o = m[a]; if (!o || v !== o.out) o = m[a] = { cs: v, out: v };
    const tt = t(o.cs); o.out = tt; if (tt !== v) el.setAttribute(a, tt);
  }
}
function walk(root: Node) {
  if (root.nodeType === 3) return trText(root as Text);
  if (root.nodeType !== 1) return;
  trAttrs(root as Element);
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n: Node | null; while ((n = w.nextNode())) { if (n.nodeType === 3) trText(n as Text); else trAttrs(n as Element); }
}
const TITLE = document.title;
export function getLang(): Lang { return LANG; }
export function setLang(l: string) {
  if (!isLang(l) || l === LANG) return;
  LANG = l; idx = IDX[l]; document.documentElement.lang = l;
  document.title = t(TITLE);
  if (document.body) walk(document.body);
  window.dispatchEvent(new CustomEvent('i18n:change', { detail: l }));
}
(window as any).I18N = { get lang() { return LANG; }, langs: LANGS, t, setLang, langOfUrl };

document.title = t(TITLE);
const start = () => {
  walk(document.body);
  new MutationObserver(ms => {
    for (const m of ms) {
      if (m.type === 'characterData') trText(m.target as Text);
      else if (m.type === 'attributes') trAttrs(m.target as Element);
      else m.addedNodes.forEach(walk);
    }
  }).observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
};
if (document.body) start(); else document.addEventListener('DOMContentLoaded', start);
const oc = window.confirm.bind(window), oa = window.alert.bind(window), op = window.prompt.bind(window);
window.confirm = (m?: string) => oc(m == null ? m : t(m));
window.alert = (m?: any) => oa(typeof m === 'string' ? t(m) : m);
window.prompt = (m?: string, d?: string) => op(m == null ? m : t(m), d);
